/**
 * Crew Layout
 *
 * Mobile-first layout with:
 * - Top header with user info
 * - Bottom navigation bar
 * - Main content area
 */

import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import OrgSwitcher from './OrgSwitcher';
import { UnreadBadge } from '../messages';
import FeedbackWidget from '../feedback/FeedbackWidget';
import OfflineBanner from '../pwa/OfflineBanner';
import { LogoMark } from '../brand/Logo';

// Navigation items for bottom nav
const navigation = [
  { name: 'Home', href: '/crew', icon: HomeIcon, enabled: true },
  { name: 'Assignments', href: '/crew/assignments', icon: ClipboardIcon, enabled: true },
  { name: 'Messages', href: '/crew/messages', icon: InboxIcon, enabled: true, showBadge: true },
  { name: 'Profile', href: '/crew/profile', icon: UserIcon, enabled: true },
];

export default function CrewLayout() {
  const { user, logout } = useAuth();
  const { hasMultipleOrgs } = useOrganization();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-paper pb-16">
      {/* Top header — dark chrome, mark centred */}
      <header className="sticky top-0 z-10 bg-rack">
        {/* Three columns: identity | mark | actions. The outer two share the
            spare width equally so the mark stays centred, and both may shrink
            to nothing (minmax(0,1fr)) and clip their text, so nothing can
            slide over the mark on a narrow phone. The mark used to be absolutely
            positioned over the row and "Send feedback" ran under it. */}
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 h-14 px-4">
          {/* Identity block links to the profile — the avatar is the natural
              "me" affordance (Dylan, 2026-08-09). */}
          <NavLink
            to="/crew/profile"
            className="flex items-center justify-self-start max-w-full min-w-0 -m-1 p-1 hover:bg-rack-raised"
            aria-label="Your profile"
          >
            <div className="w-8 h-8 shrink-0 bg-rack-unlit flex items-center justify-center text-white text-sm font-medium">
              {user?.firstName?.[0]}
              {user?.lastName?.[0]}
            </div>
            <span className="ml-2 font-medium text-white truncate">
              {user?.firstName}
            </span>
          </NavLink>
          <LogoMark size={16} on="dark" />
          <div className="flex items-center justify-end space-x-3 min-w-0 overflow-hidden">
            {hasMultipleOrgs && (
              <OrgSwitcher variant="header" />
            )}
            <FeedbackWidget buttonClassName="min-w-0 truncate text-sm text-onDark-secondary hover:text-white" />
            {/* Phones sign out from the Profile page; the header has no room. */}
            <button
              onClick={handleLogout}
              className="hidden sm:inline shrink-0 text-sm text-onDark-secondary hover:text-white"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <OfflineBanner />

      {/* Main content */}
      <main className="p-4">
        <Outlet />
      </main>

      {/* Bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-rack z-10">
        <div className="flex justify-around">
          {navigation.map((item) =>
            item.enabled ? (
              <NavLink
                key={item.name}
                to={item.href}
                end={item.href === '/crew'}
                className={({ isActive }) =>
                  `flex flex-col items-center py-2 px-3 text-xs relative border-t-[3px] ${
                    isActive
                      ? 'border-white text-white'
                      : 'border-transparent text-onDark-muted hover:text-white'
                  }`
                }
              >
                <div className="relative">
                  <item.icon className="w-6 h-6 mb-1" />
                  {'showBadge' in item && item.showBadge && (
                    <UnreadBadge className="absolute -top-1 -right-2" />
                  )}
                </div>
                {item.name}
              </NavLink>
            ) : (
              <div
                key={item.name}
                className="flex flex-col items-center py-2 px-3 text-xs border-t-[3px] border-transparent text-rack-unlit cursor-not-allowed"
              >
                <item.icon className="w-6 h-6 mb-1" />
                {item.name}
              </div>
            )
          )}
        </div>
      </nav>
    </div>
  );
}

// =============================================================================
// Icons (inline SVG components)
// =============================================================================

function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}

function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  );
}

function InboxIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}
